import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { getSpecialities, listVendors, exportVendorsCsv, listOf } from './vendorsApi';
import { errorMessage, toastError } from '../../lib/toast';
import { VENDOR_ERRORS, holdsLevel } from './vendorLabels';
import { Skeleton, FullError } from './VendorBits';
import VendorRow from './VendorRow';
import VendorFilterSidebar from './VendorFilterSidebar';
import './Vendors.css';
import '../directory/directory.css';
import './VendorList.css';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const NO_FILTERS = { tagIds: [], risk: '', review: '', attention: false, archived: false };

const hasAnyAccess = (me) => !!me && (!!me.is_admin || !!me.see_all || !!me.triage
  || (me.levels || []).length > 0 || (me.owner_tag_ids || []).length > 0);

// Only set filters go on the wire, so the server's defaults stay the defaults.
function buildParams({ q, tagIds, risk, review, attention, archived, page }) {
  const params = { page };
  if (q) params.q = q;
  if (tagIds.length) params.tag = tagIds.join(',');
  if (risk) params.risk = risk;
  if (review) params.review = review;
  if (attention) params.attention = 1;
  if (archived) params.archived = 1;
  return params;
}

// `facets` is optional on the wire: absent (older server, or a failed count read)
// means "unknown", which the rail shows as no number rather than zero.
function readCounts(d) {
  const list = d?.facets?.specialities;
  if (!Array.isArray(list)) return null;
  return new Map(list.map((f) => [f.id, f.count]));
}

const countFilters = (f) => f.tagIds.length + (f.risk ? 1 : 0) + (f.review ? 1 : 0)
  + (f.attention ? 1 : 0) + (f.archived ? 1 : 0);

/**
 * The searchable vendor register (spec 4.2): a filter rail of specialities and
 * statuses beside a ruled list of vendor rows, laid out like the Employee Directory.
 * A failed read is a full-page error with Retry: it is never rendered as an
 * empty list.
 */
export default function VendorList({ me, onRequestAccess, onAddVendor }) {
  const accessOk = hasAnyAccess(me);
  const canExport = !!me && (!!me.see_all || !!me.is_admin);
  const canAdd = !!onAddVendor && holdsLevel(me, 'propose');

  const [search, setSearch] = useState('');
  const q = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const [filters, setFilters] = useState(NO_FILTERS);
  const { tagIds, risk, review, attention, archived } = filters;
  const [page, setPage] = useState(1);
  const [specs, setSpecs] = useState({ status: 'loading', items: [] });

  const [state, setState] = useState({ status: 'loading', rows: [], total: 0, counts: null });
  const reqId = useRef(0);

  // Filters other than the page reset to page 1.
  const filtersKey = JSON.stringify([q, tagIds, risk, review, attention, archived]);
  const lastFilters = useRef(filtersKey);
  useEffect(() => {
    if (lastFilters.current !== filtersKey) {
      lastFilters.current = filtersKey;
      setPage(1);
    }
  }, [filtersKey]);

  const specsLive = useRef(true);
  const loadSpecialities = useCallback(() => {
    setSpecs((s) => ({ ...s, status: 'loading' }));
    getSpecialities()
      .then((d) => { if (specsLive.current) setSpecs({ status: 'ready', items: listOf(d, 'specialities') }); })
      // A failed read says so in the rail; it must not read as "no specialities".
      .catch(() => { if (specsLive.current) setSpecs({ status: 'error', items: [] }); });
  }, []);
  useEffect(() => {
    specsLive.current = true;
    if (accessOk) loadSpecialities();
    return () => { specsLive.current = false; };
  }, [accessOk, loadSpecialities]);

  const load = useCallback(() => {
    const id = ++reqId.current;
    setState((s) => ({ ...s, status: 'loading' }));
    listVendors(buildParams({ q, tagIds, risk, review, attention, archived, page }))
      .then((d) => {
        if (id !== reqId.current) return;
        const rows = listOf(d, 'vendors').length ? listOf(d, 'vendors') : listOf(d, 'rows');
        setState({ status: 'ready', rows, total: d?.total ?? 0, counts: readCounts(d) });
      })
      .catch(() => {
        if (id !== reqId.current) return;
        // Counts from an earlier response would describe a different query: drop them.
        setState({ status: 'error', rows: [], total: 0, counts: null });
      });
    // filtersKey covers q/tagIds/risk/review/attention/archived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, page]);

  useEffect(() => {
    if (accessOk) load();
  }, [accessOk, load]);

  // A blob download, so a 403/429 is a toast instead of a navigation to JSON.
  const downloadExport = async () => {
    try {
      const { blob, filename } = await exportVendorsCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(errorMessage(err, 'Could not export the vendor register.', VENDOR_ERRORS));
    }
  };

  const patchFilters = useCallback((patch) => setFilters((f) => ({ ...f, ...patch })), []);
  const toggleTag = useCallback((id) => setFilters((f) => ({
    ...f, tagIds: f.tagIds.includes(id) ? f.tagIds.filter((t) => t !== id) : [...f.tagIds, id],
  })), []);
  const clearFilters = useCallback(() => setFilters(NO_FILTERS), []);
  const clearAll = () => { setSearch(''); setFilters(NO_FILTERS); };

  const activeCount = countFilters(filters);
  const filtered = !!(q || activeCount);
  const pages = Math.max(1, Math.ceil(state.total / PAGE_SIZE));

  // Without access, asking for it is the only thing to do here, so it leads the
  // empty state. With access it is a quiet text action beside the real work.
  if (!accessOk) {
    return (
      <div className="vd-empty">
        <p>You don&apos;t have access to any vendor specialities yet.</p>
        <button type="button" className="btn btn-primary" onClick={onRequestAccess}>Request access</button>
      </div>
    );
  }

  const firstLoad = state.status === 'loading' && state.rows.length === 0;
  let body;
  if (firstLoad) {
    body = <Skeleton label="Loading vendors" />;
  } else if (state.status === 'error') {
    body = <FullError message="Couldn't load vendors. Try again in a minute." onRetry={load} />;
  } else if (state.rows.length === 0) {
    body = (
      <div className="vd-empty">
        <p>{filtered ? 'No vendors match these filters.' : 'No vendors in your specialities yet.'}</p>
        {filtered && <button type="button" className="btn btn-secondary btn-small" onClick={clearAll}>Clear filters</button>}
        {!filtered && canAdd && <button type="button" className="btn btn-primary" onClick={onAddVendor}>Add vendor</button>}
      </div>
    );
  } else {
    body = (
      <>
        {/* One white panel, ruled rows. The header row is visual only: each row
            carries its own labels (dt) for assistive tech, and the header is
            hidden below 1024px where the readings collapse into a line. */}
        <div className="vd-panel vl-listpanel">
          <div className="vl-head" aria-hidden="true">
            <span>Vendor</span>
            <span>Risk</span>
            <span>NDA</span>
            <span>Contract</span>
            <span>Next review</span>
          </div>
          <h2 className="sr-only" id="vl-results-heading">Vendors</h2>
          <ul
            className={`vl-rows${state.status === 'loading' ? ' is-refreshing' : ''}`}
            role="list"
            aria-labelledby="vl-results-heading"
          >
            {state.rows.map((v) => <VendorRow key={v.id} vendor={v} />)}
          </ul>
        </div>
        {pages > 1 && (
          <nav className="vd-pager" aria-label="Pagination">
            <button type="button" className="btn btn-secondary btn-small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {page} of {pages}</span>
            <button type="button" className="btn btn-secondary btn-small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        )}
      </>
    );
  }

  return (
    <div className="vl">
      <a
        className="dir-skip btn btn-secondary btn-small"
        href="#vl-search"
        onClick={(e) => { e.preventDefault(); document.getElementById('vl-search')?.focus(); }}
      >
        Skip to vendor search
      </a>
      <div className="dir-layout vl-layout">
        <VendorFilterSidebar
          specialities={specs.items}
          specialitiesStatus={specs.status}
          onRetrySpecialities={loadSpecialities}
          counts={state.counts}
          filters={filters}
          onChange={patchFilters}
          onToggleTag={toggleTag}
          onClearAll={clearFilters}
          activeCount={activeCount}
        />

        <div className="dir-main">
          <div className="vd-toolbar vl-toolbar">
            <input
              id="vl-search"
              type="search"
              className="search-input"
              placeholder="Search name, speciality, tax ID or email"
              aria-label="Search vendors"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="vd-actions">
              <button type="button" className="vd-text-btn" onClick={onRequestAccess}>Request access</button>
              {canExport && (
                <button type="button" className="btn btn-secondary vd-btn-compact" onClick={downloadExport}>Export CSV</button>
              )}
              {canAdd && <button type="button" className="btn btn-primary vd-btn-compact" onClick={onAddVendor}>Add vendor</button>}
            </div>
          </div>

          <p className="dir-resultcount" role="status" aria-live="polite">
            {/* Hidden with the empty state, as in the directory: "0 vendors" above
                "No vendors match" says the same thing twice. */}
            {state.status === 'ready' && state.total > 0 && (
              <>
                {state.total === 1 ? '1 vendor' : `${state.total.toLocaleString('en-GB')} vendors`}
                {pages > 1 && ` · showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, state.total)}`}
              </>
            )}
          </p>

          <div className="vl-results" aria-busy={state.status === 'loading'}>
            {body}
          </div>
        </div>
      </div>
    </div>
  );
}
